import { doc, updateDoc } from 'firebase/firestore';
import { apiRequest } from './apiRequest';
import { db, auth } from '../firebaseConfig';
import { ParsedZugferdData, ReceivedInvoice } from '../types/receivedInvoiceTypes';

const RECEIVED_INVOICES_COLLECTION = 'received_invoices';

export const parseZugferdPdf = async (
  base64Pdf: string
): Promise<ParsedZugferdData> => {
  const response = await apiRequest('/parse-zugferd-pdf', {
    method: 'POST',
    body: JSON.stringify({ pdfBase64: base64Pdf }),
  });

  if (!response.ok) {
    let errorMsg = 'Fehler beim Einlesen der PDF';
    try {
      const errorJson = await response.json();
      if (errorJson.error) errorMsg = errorJson.error;
    } catch {
      // ignore
    }
    throw new Error(errorMsg);
  }

  const json = await response.json();
  return json.data as ParsedZugferdData;
};

// ─── markReceivedInvoiceDiscarded (M-V4-D2, architecture-ux-v4.md §4.3) ────────

/**
 * Fail-closed auth guard (Muster `scannedReceiptsApi.requireCurrentUser`):
 * throws if no user is signed in (Golden Rule — never an anonymous scope). No
 * scope uid is returned: the discard write targets a document by id and tenant
 * scope is enforced server-side by the `received_invoices` Firestore Rule.
 *
 * @throws {Error} if there is no authenticated user.
 */
function requireCurrentUser(): void {
  if (!auth.currentUser) {
    throw new Error(
      'receivedInvoicesApi: No authenticated user. Sign in before modifying received invoices.',
    );
  }
}

/**
 * Soft-discards an upload-channel entry (M-V4-D2, §4.3).
 *
 * Writes `discardedAt` (current ISO-8601 timestamp) onto the
 * `received_invoices/{id}` document. This is a display-only classification — the
 * original document and any archived PDF stay untouched (client deletes are
 * forbidden: `allow delete: if false`, §14b UStG / GoBD retention). The Rule
 * permits a tenant-scoped `update` without a field allowlist, so the discard is
 * a plain field write. The row subsequently derives `UnifiedStatus ===
 * 'discarded'` (see `deriveUnifiedStatus`).
 *
 * Idempotent by intent: re-discarding overwrites the timestamp — harmless.
 *
 * @param receivedInvoiceId - Firestore document ID of the received invoice.
 * @throws {Error} If not authenticated or the update fails.
 */
export async function markReceivedInvoiceDiscarded(
  receivedInvoiceId: string,
): Promise<void> {
  requireCurrentUser();

  const docRef = doc(db, RECEIVED_INVOICES_COLLECTION, receivedInvoiceId);
  await updateDoc(docRef, {
    discardedAt: new Date().toISOString(),
  } as Partial<ReceivedInvoice>);
}
